import { useCallback, useEffect, useRef, useState } from 'react'
import toast from 'utils/toast'
import { useTemporalWorkflowStatus } from 'hooks/useTemporalWorkflowStatus'

type MarketRun = { workflowId: string; runId: string | null; accessToken: string | null }

export type MarketRunResponse = {
    workflow_id?: string | null
    run_id?: string | null
    access_token?: string | null
}

type MarketRunOptions = {
    workflowClass: string
    eventBusTopic: string
    /** Toasted when the run ends in an error without a message of its own. */
    failureMessage: string
    /** Called once when the run ends, whichever way. */
    onFinished?: (outcome: 'completed' | 'failed') => void
}

/**
 * The narration of one ordinal market workflow run (a sale settlement, or a
 * local-signing order authorization) for the surface that started it. Live
 * progress arrives on the event bus; polling is the durable fallback. The run
 * can end after the surface has already moved on, so a failure gets a toast.
 */
export function useOrdinalMarketRunStatus({
    workflowClass,
    eventBusTopic,
    failureMessage,
    onFinished,
}: MarketRunOptions) {
    const [run, setRun] = useState<MarketRun | null>(null)
    const notifiedErrorRun = useRef<string | null>(null)
    const finishedRun = useRef<string | null>(null)
    const pollingRun = useRef<string | null>(null)
    const runKey = run ? `${run.workflowId}:${run.runId || ''}` : null

    const finish = useCallback(
        (outcome: 'completed' | 'failed') => {
            if (runKey && finishedRun.current === runKey) return
            finishedRun.current = runKey
            onFinished?.(outcome)
        },
        [onFinished, runKey],
    )

    // Stable identity: the status hook resubscribes when its callbacks change.
    const handleComplete = useCallback(() => finish('completed'), [finish])

    const notifyError = useCallback(
        (message: string) => {
            if (!runKey || notifiedErrorRun.current !== runKey) {
                notifiedErrorRun.current = runKey
                toast.error(message || failureMessage)
            }
            finish('failed')
        },
        [failureMessage, finish, runKey],
    )

    const { latestMessage, startPolling } = useTemporalWorkflowStatus(
        run?.workflowId || null,
        run?.runId || null,
        'progress',
        workflowClass,
        {
            enabled: !!run?.workflowId && !!run?.runId,
            eventBusTopic,
            accessToken: run?.accessToken || undefined,
            onWorkflowError: notifyError,
            onComplete: onFinished ? handleComplete : undefined,
            continuePollingOnError: true,
        },
    )

    // Live progress is the fast path. Polling is the durable fallback for a
    // terminal frame emitted before this component subscribes or during a
    // socket gap.
    useEffect(() => {
        if (!run?.workflowId || !run.runId || !runKey) return
        if (pollingRun.current === runKey) return

        pollingRun.current = runKey
        startPolling()
    }, [run?.runId, run?.workflowId, runKey, startPolling])

    /** Follow the run a response started. Returns false when it cannot be followed. */
    const trackRun = useCallback((response: MarketRunResponse | null | undefined) => {
        if (!response?.workflow_id) return false
        setRun({
            workflowId: response.workflow_id,
            runId: response.run_id || null,
            accessToken: response.access_token || null,
        })
        return !!response.run_id
    }, [])

    return { message: latestMessage, trackRun }
}
